<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minValue, maxValue } from '@vuelidate/validators';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      clientId: '',
      clientSecret: '',
      redirectUri: `${window.location.origin}/olx/oauth/callback`,
      lang: 'uk_UA',
      partnerCode: '',
      pollingInterval: 60,
      createdInboxId: null,
      authorizeUrl: null,
      langOptions: [
        { value: 'uk_UA', label: 'Ukraine (uk_UA)' },
        { value: 'pl_PL', label: 'Poland (pl_PL)' },
        { value: 'ro_RO', label: 'Romania (ro_RO)' },
        { value: 'bg_BG', label: 'Bulgaria (bg_BG)' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations: {
    clientId: { required },
    clientSecret: { required },
    redirectUri: { required },
    pollingInterval: {
      required,
      minValue: minValue(30),
      maxValue: maxValue(300),
    },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const olxChannel = await this.$store.dispatch('inboxes/createChannel', {
          channel: {
            type: 'olx',
            client_id: this.clientId,
            client_secret: this.clientSecret,
            redirect_uri: this.redirectUri,
            lang: this.lang,
            partner_code: this.partnerCode,
            polling_interval: this.pollingInterval,
          },
        });
        this.createdInboxId = olxChannel.id;
        const channelId = olxChannel.channel_id || olxChannel.channel?.id;
        const state = `${channelId}:${Math.random().toString(36).slice(2)}`;
        const params = new URLSearchParams({
          client_id: this.clientId,
          response_type: 'code',
          state,
          scope: 'read write v2',
          redirect_uri: this.redirectUri,
        });
        this.authorizeUrl = `https://www.olx.ua/oauth/authorize/?${params.toString()}`;
        window.location.href = this.authorizeUrl;
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.OLX_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.OLX_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.OLX_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.clientId.$error }">
          {{ $t('INBOX_MGMT.ADD.OLX_CHANNEL.CLIENT_ID.LABEL') }}
          <input
            v-model="clientId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.OLX_CHANNEL.CLIENT_ID.PLACEHOLDER')
            "
            @blur="v$.clientId.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.clientSecret.$error }">
          {{ $t('INBOX_MGMT.ADD.OLX_CHANNEL.CLIENT_SECRET.LABEL') }}
          <input
            v-model="clientSecret"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.OLX_CHANNEL.CLIENT_SECRET.PLACEHOLDER')
            "
            @blur="v$.clientSecret.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.redirectUri.$error }">
          {{ $t('INBOX_MGMT.ADD.OLX_CHANNEL.REDIRECT_URI.LABEL') }}
          <input v-model="redirectUri" type="text" />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.OLX_CHANNEL.REDIRECT_URI.SUBTITLE') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label>
          {{ $t('INBOX_MGMT.ADD.OLX_CHANNEL.LANG.LABEL') }}
          <select v-model="lang">
            <option
              v-for="opt in langOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label>
          {{ $t('INBOX_MGMT.ADD.OLX_CHANNEL.PARTNER_CODE.LABEL') }}
          <input v-model="partnerCode" type="text" />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.pollingInterval.$error }">
          {{ $t('INBOX_MGMT.ADD.OLX_CHANNEL.POLLING_INTERVAL.LABEL') }}
          <input
            v-model.number="pollingInterval"
            type="number"
            min="30"
            max="300"
            @blur="v$.pollingInterval.$touch"
          />
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.OLX_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
